import {
	ButtonHTMLAttributes,
	forwardRef,
	type ReactNode,
} from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

// =====================
// Variants
// =====================

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				primary: "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm hover:shadow",
				secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
				ghost: "bg-transparent hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-800 dark:active:bg-gray-700",
				outline: "border-2 border-gray-300 bg-transparent hover:bg-gray-50 active:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800 dark:active:bg-gray-700",
				danger: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm",
				success: "bg-green-600 text-white hover:bg-green-700 active:bg-green-800 shadow-sm",
				link: "text-brand-600 hover:text-brand-700 underline-offset-4 hover:underline",
			},
			size: {
				xs: "h-7 px-2.5 text-xs rounded-md",
				sm: "h-8 px-3 text-sm rounded-md",
				md: "h-10 px-4 text-sm rounded-lg",
				lg: "h-12 px-6 text-base rounded-lg",
				xl: "h-14 px-8 text-lg rounded-xl",
				icon: "h-10 w-10 rounded-lg",
				"icon-sm": "h-8 w-8 rounded-md",
				"icon-lg": "h-12 w-12 rounded-lg",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "md",
		},
	}
);

// =====================
// Types
// =====================

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	loading?: boolean;
	leftIcon?: ReactNode;
	rightIcon?: ReactNode;
	fullWidth?: boolean;
} & Parameters<typeof buttonVariants>[0];

// =====================
// Component
// =====================

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, loading, leftIcon, rightIcon, fullWidth, disabled, children, ...props }, ref) => {
		return (
			<button
				ref={ref}
				className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
				disabled={disabled || loading}
				{...props}
			>
				{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : leftIcon}
				{children}
				{!loading && rightIcon}
			</button>
		);
	}
);

Button.displayName = "Button";

export { Button, buttonVariants };